import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Music, Youtube, Instagram, Facebook, ExternalLink, Disc, Headphones, Users, Globe, Twitter } from 'lucide-react';
import ShareButton from '../components/ShareButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const About = () => {
  const { theme, isDarkMode } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/artist-profiles`);
      setProfiles(res.data || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const GlassCard = ({ children, className = '' }) => (
    <div 
      className={`backdrop-blur-xl rounded-xl p-4 sm:p-6 ${className}`}
      style={{ 
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
      }}
    >
      {children}
    </div>
  );

  const SocialLink = ({ href, icon: Icon, label, color }) => {
    if (!href) return null;
    return (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg hover:bg-white/10 transition-colors"
      >
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color }}>
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
        <span className="text-sm sm:text-base" style={{ color: 'var(--text-primary)' }}>{label}</span>
        <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 ml-auto flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
      </a>
    );
  };

  // Get profile icon based on index
  const getProfileIcon = (index) => {
    const icons = [Headphones, Music, Disc];
    return icons[index % icons.length];
  };

  // Get profile color based on index
  const getProfileColor = (index) => {
    return index % 2 === 0 ? theme.primary : theme.secondary;
  };

  return (
    <div className="py-4">
      <div className="max-w-5xl mx-auto px-3 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <motion.h1 
            className="text-2xl sm:text-3xl font-black uppercase tracking-wider"
            style={{ color: theme.primary, textShadow: isDarkMode ? theme.glow : 'none' }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="about-title"
          >
            About Us
          </motion.h1>
          <ShareButton title="About BeatLab52" text="Learn about DJ808 & CRG Productionz - the creators of BeatLab52!" />
        </div>

        <motion.div 
          className="space-y-6 sm:space-y-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {/* BeatLab52 Story */}
          <GlassCard>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme.primary}20`, border: `2px solid ${theme.primary}` }}>
                <Disc className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: theme.primary }} />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold" style={{ color: theme.primary }}>BeatLab52</h2>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Where Beats Come Alive</p>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4 text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
              <p>
                <strong style={{ color: theme.primary }}>BeatLab52</strong> is the ultimate collaborative music platform born from the creative partnership between two passionate music creators: <strong>DJ808</strong> and <strong>CRG Productionz</strong>.
              </p>
              <p>
                United by their love for music production, DJing, and pushing creative boundaries, these two artists joined forces to create a space where beats, creativity, and community come together. BeatLab52 represents their shared vision of a futuristic music experience powered by cutting-edge technology and authentic artistry.
              </p>
              <p className="hidden sm:block">
                From live radio streams to AI-powered music creation, photo galleries to community chat, BeatLab52 is more than just a website – it's a movement. A place where fans can connect, discover new music, and be part of the journey.
              </p>
            </div>
          </GlassCard>

          {/* Artist Profiles - Dynamic from API */}
          {loading ? (
            <GlassCard className="text-center py-8">
              <div className="animate-pulse flex flex-col items-center gap-4">
                <div className="w-24 h-24 rounded-full bg-white/10" />
                <div className="h-6 w-48 bg-white/10 rounded" />
                <div className="h-4 w-32 bg-white/10 rounded" />
              </div>
            </GlassCard>
          ) : (
            profiles.map((profile, index) => {
              const ProfileIcon = getProfileIcon(index);
              const profileColor = getProfileColor(index);
              
              return (
                <GlassCard key={profile.artist_name}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    <div className="md:col-span-1">
                      {/* Profile Image or Icon */}
                      <div className="aspect-square rounded-xl overflow-hidden mb-3 sm:mb-4 max-w-[200px] mx-auto md:max-w-none" style={{ backgroundColor: `${profileColor}20` }}>
                        {profile.image_url ? (
                          <img 
                            src={profile.image_url} 
                            alt={profile.display_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className={`w-full h-full ${profile.image_url ? 'hidden' : 'flex'} items-center justify-center`}>
                          <ProfileIcon className="w-16 h-16 sm:w-24 sm:h-24" style={{ color: profileColor }} />
                        </div>
                      </div>
                      
                      {/* Name and Tagline */}
                      <div className="text-center md:text-left">
                        <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{ color: profileColor }}>
                          {profile.display_name}
                        </h2>
                        {profile.role && (
                          <p className="text-xs sm:text-sm font-semibold mb-1" style={{ color: profileColor, opacity: 0.8 }}>
                            {profile.role}
                          </p>
                        )}
                        {profile.tagline && profile.tagline !== profile.role && (
                          <p className="text-xs sm:text-sm mb-3 sm:mb-4" style={{ color: 'var(--text-muted)' }}>
                            {profile.tagline}
                          </p>
                        )}
                        {(!profile.tagline || profile.tagline === profile.role) && <div className="mb-3 sm:mb-4" />}
                      </div>
                      
                      {/* Social Links */}
                      <div className="space-y-1 sm:space-y-2">
                        <SocialLink href={profile.youtube_url} icon={Youtube} label="YouTube" color="#FF0000" />
                        <SocialLink href={profile.instagram_url} icon={Instagram} label="Instagram" color="#E4405F" />
                        <SocialLink href={profile.facebook_url} icon={Facebook} label="Facebook" color="#1877F2" />
                        <SocialLink href={profile.twitter_url} icon={Twitter} label="Twitter/X" color="#1DA1F2" />
                        <SocialLink href={profile.soundcloud_url} icon={Music} label="SoundCloud" color="#FF5500" />
                        <SocialLink href={profile.spotify_url} icon={Music} label="Spotify" color="#1DB954" />
                        <SocialLink href={profile.website_url} icon={Globe} label="Website" color={profileColor} />
                      </div>
                    </div>
                    
                    {/* Bio */}
                    <div className="md:col-span-2 space-y-3 sm:space-y-4 text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
                      {profile.bio?.split('\n\n').map((paragraph, pIndex) => (
                        <p key={pIndex} className={pIndex > 1 ? 'hidden sm:block' : ''}>
                          {pIndex === 0 ? (
                            <>
                              <strong style={{ color: profileColor }}>{profile.display_name}</strong>{' '}
                              {paragraph.replace(profile.display_name, '').trim()}
                            </>
                          ) : paragraph}
                        </p>
                      ))}
                      
                      {/* Genres */}
                      {profile.genres && profile.genres.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {profile.genres.map((genre, gIndex) => (
                            <span 
                              key={gIndex}
                              className="px-3 py-1 rounded-full text-xs font-medium"
                              style={{ backgroundColor: `${profileColor}20`, color: profileColor }}
                            >
                              {genre}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </GlassCard>
              );
            })
          )}

          {/* The Partnership */}
          <GlassCard className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.primary}20`, border: `2px solid ${theme.primary}` }}>
                <Users className="w-7 h-7 sm:w-8 sm:h-8" style={{ color: theme.primary }} />
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" style={{ color: theme.primary }}>The Partnership</h2>
            <div className="max-w-2xl mx-auto space-y-3 sm:space-y-4 text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
              <p>
                When <strong style={{ color: theme.primary }}>DJ808</strong> and <strong style={{ color: theme.secondary }}>CRG Productionz</strong> came together, they knew they had something special. Their complementary skills, shared passion, and drive to innovate created the perfect foundation for BeatLab52.
              </p>
              <p>
                This platform represents their commitment to the music community – a place where technology meets creativity, where fans become family, and where the next generation of beats is being born.
              </p>
            </div>
          </GlassCard>

          {/* Call to Action */}
          <GlassCard className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" style={{ color: theme.primary }}>Join the Movement</h2>
            <p className="mb-4 sm:mb-6 text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>
              Follow us on social media, tune into our radio stream, and become part of the BeatLab52 family.
            </p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              <a 
                href="/radio" 
                className="px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-bold text-black text-sm sm:text-base"
                style={{ backgroundColor: theme.primary }}
              >
                Listen to Radio
              </a>
              <a 
                href="/music" 
                className="px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-bold text-sm sm:text-base"
                style={{ backgroundColor: 'transparent', border: `2px solid ${theme.primary}`, color: theme.primary }}
              >
                Browse Music
              </a>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
